import {useMutation} from "react-query";
import {Admission} from "@reside/reside-api-admission";
import Alert from "react-s-alert";
import {
  AdmissionStatus,
  AdmissionsService,
} from "../../../services/AdmissionsService";
import {readResidentNameAnswer} from "../../../models/AdmissionModel.helpers";
import {useAdmissionsRestoreModalContext} from "./context";
import {RestoreAdmissionModal} from "../../restore-admission-modal";
import {
  getWorkflowLabel,
  isPendingWorkflowType,
} from "../../start-workflow/workflowMapping";

type StateProps = Readonly<{
  onRestored?: (admissionId: string, status: Admission["status"]) => void;
}>;

/**
 * Restore flips CANCELLED back to the status from before cancellation, so a body
 * that omits `status` still has one value search can be polled for.
 */
const statusBeforeCancellation = async (
  admissionId: string,
): Promise<Admission["status"] | undefined> => {
  try {
    const transitions = await AdmissionsService.transitions(admissionId);
    const previous = transitions.data?.find(
      transition =>
        !!transition.status &&
        (transition.status as string) !== AdmissionStatus.CANCELLED,
    )?.status;
    return previous as Admission["status"] | undefined;
  } catch {
    return undefined;
  }
};

export const AdmissionsRestoreAdmissionModal = ({onRestored}: StateProps) => {
  const {admission, toggle} = useAdmissionsRestoreModalContext();

  const {mutate, isLoading} = useMutation(
    ({admissionId}: {admissionId: string}) =>
      AdmissionsService.restore(admissionId),
    {
      onError: () => {
        // "Pending workflow" is stored capitalized (it reads fine as a label), but needs
        // lowercasing mid-sentence; "Admission"/"Arbitration" already read fine as-is.
        const label = getWorkflowLabel(admission?.workflowType);
        const isPending = isPendingWorkflowType(admission?.workflowType);
        const midSentenceLabel = isPending ? label.toLowerCase() : label;
        Alert.error(`Failed to restore the ${midSentenceLabel}`);
      },
      onSuccess: async (restored, {admissionId}) => {
        const label = getWorkflowLabel(admission?.workflowType);
        const isPending = isPendingWorkflowType(admission?.workflowType);
        const sentence = isPending ? `The ${label.toLowerCase()}` : label;
        Alert.success(`${sentence} was successfully restored.`);
        const status =
          restored.data?.status ??
          (admissionId
            ? await statusBeforeCancellation(admissionId)
            : undefined);
        if (admissionId && status) {
          onRestored?.(admissionId, status);
        }
      },
      onSettled: () => {
        toggle();
      },
    },
  );

  return (
    <RestoreAdmissionModal
      isOpen={!!admission}
      outdatedContract={admission?.outdatedContract}
      admissionId={admission?.id}
      admissionCode={admission?.code}
      residentName={readResidentNameAnswer(admission)}
      isLoading={isLoading}
      onCancel={() => mutate({admissionId: admission?.id})}
      onRequestClose={() => toggle()}
      admissionStatus={admission?.status}
      workflowType={admission?.workflowType}
    />
  );
};
